import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts/TenantProvider'
import { supabase } from '@/lib/supabase'
import {
  ORIGIN_PLAN_LIMITS,
  ORIGIN_PLAN_ORDER,
  normalizeOriginPlanTier,
  type OriginPlanTier,
  type PricingLimits,
} from '@/lib/origin-pricing'

export type { PricingLimits } from '@/lib/origin-pricing'

/* ============================================================
   usePricingTier — Feature gating by tenant plan level
   PRD §36 (Pricing Tiers & Business Model)
   
   Reads the tenant's plan from the tenants table and exposes
   boolean gates for each feature category. Components can use
   these to conditionally render or disable premium features.
   ============================================================ */

export type PlanTier = OriginPlanTier

interface PricingTierResult {
  tier: PlanTier
  limits: PricingLimits
  isLoading: boolean
  /** Check if a specific feature is available */
  hasFeature: (feature: keyof PricingLimits) => boolean
  /** Check if a numeric limit is within bounds */
  withinLimit: (feature: keyof PricingLimits, current: number) => boolean
  /** Minimum tier required for a feature */
  requiredTier: (feature: keyof PricingLimits) => PlanTier
}

export function usePricingTier(): PricingTierResult {
  const { tenant } = useTenant()
  const tenantId = tenant?.id

  const { data: tenantPlan, isLoading } = useQuery<PlanTier>({
    queryKey: ['tenant_plan', tenantId],
    queryFn: async () => {
      if (!tenantId) return 'trial'
      const { data, error } = await supabase
        .from('tenants')
        .select('plan')
        .eq('id', tenantId)
        .single()
      if (error) return 'trial'
      const row = data as Record<string, unknown> | null
      return normalizeOriginPlanTier(row?.plan as string | null | undefined)
    },
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 min — plan changes are rare
  })

  const tier = normalizeOriginPlanTier(tenantPlan)
  const limits = ORIGIN_PLAN_LIMITS[tier]

  const hasFeature = useMemo(
    () =>
      (feature: keyof PricingLimits): boolean => {
        const value = limits[feature]
        if (typeof value === 'boolean') return value
        if (typeof value === 'number') return value > 0
        return false
      },
    [limits]
  )

  const withinLimit = useMemo(
    () =>
      (feature: keyof PricingLimits, current: number): boolean => {
        const value = limits[feature]
        if (typeof value === 'number') return current < value
        return true
      },
    [limits]
  )

  const requiredTier = useMemo(
    () =>
      (feature: keyof PricingLimits): PlanTier => {
        for (const t of ORIGIN_PLAN_ORDER) {
          const val = ORIGIN_PLAN_LIMITS[t][feature]
          if (typeof val === 'boolean' && val) return t
          if (typeof val === 'number' && val > 0) return t
        }
        return 'enterprise'
      },
    []
  )

  return { tier, limits, isLoading, hasFeature, withinLimit, requiredTier }
}
